class VideoPlayer extends HTMLElement {
  constructor () {
    super();
    // Initialize the loaded state as a boolean rather than a string
    this.loaded = this.getAttribute('loaded') === 'true' || false;
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    this.video = this.querySelector('.main-video');
    this.centerPlayButton = this.querySelector('.leo-center-play-button');
    this.controls = this.querySelector('.leo-custom-video-controls');
    this.loadingSpinner = this.querySelector('.leo-loading-spinner');
    this.video.setAttribute('playsinline', true); // For iOS Safari

    // Store bound functions to ensure event removal works correctly
    this._loadContentBound = this.loadContent.bind(this);
    this._togglePlayBound = this.togglePlay.bind(this);
    this._updateProgressBarBound = this.updateProgressBar.bind(this);
    this._updatePlayButtonBound = this.updatePlayButton.bind(this);
    this._updateControlsVisibilityBound = this.updateControlsVisibility.bind(this);
    this._updateVolumeIconBound = this.updateVolumeIcon.bind(this);
    this._handleDoubleClickBound = this.handleDoubleClick.bind(this);
    this._seekBackwardBound = (event) => this.seek(-15, event);
    this._seekForwardBound = (event) => this.seek(15, event);
    this._scrubBound = this.scrub.bind(this);
    this._toggleMuteBound = this.toggleMute.bind(this);
    this._handleVolumeChangeBound = this.handleVolumeChange.bind(this);
    this._togglePictureInPictureBound = this.togglePictureInPicture.bind(this);
    this._showControlsBound = this.showControls.bind(this);
    this._hideControlsBound = this.hideControls.bind(this);
    this._updateVolumeSliderBound = this.updateVolumeSlider.bind(this);
    this._showVolumePercentageBound = this.showVolumePercentage.bind(this);
    this._hideVolumePercentageBound = this.hideVolumePercentage.bind(this);
    this._handleVideoBoundingBound = this.handleVideoBuffering.bind(this);
    this._handleVolumeWheelBound = this.handleVolumeWheel.bind(this);
    this._toogleVideo = this.toogleVideo.bind(this);
  }

  connectedCallback() {
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    if (!this.video) return;

    if (this.poster) {
      // Use the bound method to ensure proper context
      this.poster.addEventListener('click', this._loadContentBound);
    }

    // Add click event to the video for play/pause (YouTube-like behavior)
    this.video.addEventListener('click', this._togglePlayBound);

    this.video.addEventListener('timeupdate', this._updateProgressBarBound);
    this.video.addEventListener('play', this._updatePlayButtonBound);
    this.video.addEventListener('pause', this._updatePlayButtonBound);
    this.video.addEventListener('volumechange', this._updateVolumeIconBound);

    // Buffering detection
    this.video.addEventListener('waiting', this._handleVideoBoundingBound);
    this.video.addEventListener('playing', this._handleVideoBoundingBound);
    this.video.addEventListener('canplay', this._handleVideoBoundingBound);

    // Update control visibility when video state changes
    this.video.addEventListener('play', this._updateControlsVisibilityBound);
    this.video.addEventListener('pause', this._updateControlsVisibilityBound);

    // Double click events for seeking
    this.video.addEventListener('dblclick', this._handleDoubleClickBound);

    if (this.centerPlayButton) {
      this.centerPlayButton.addEventListener('click', this._togglePlayBound);
    }

    if (this.controls) {
      // Play/Pause button
      const playButton = this.controls.querySelector('.video-play-button');
      if (playButton) {
        playButton.addEventListener('click', this._togglePlayBound);
      }

      // Seek buttons
      const seekBackward = this.controls.querySelector('.video-seek-backward');
      if (seekBackward) {
        seekBackward.addEventListener('click', this._seekBackwardBound);
      }

      const seekForward = this.controls.querySelector('.video-seek-forward');
      if (seekForward) {
        seekForward.addEventListener('click', this._seekForwardBound);
      }

      // Progress bar
      const progressContainer = this.controls.querySelector('.leo-video-progress-container');
      if (progressContainer) {
        progressContainer.addEventListener('click', this._scrubBound);
      }

      // Volume control
      const volumeControl = this.controls.querySelector('.video-volume-control');
      if (volumeControl) {
        const volumeButton = volumeControl.querySelector('.video-volume-button');
        const volumeSlider = volumeControl.querySelector('.video-volume-slider');

        if (volumeButton) {
          volumeButton.addEventListener('click', this._toggleMuteBound);
        }

        if (volumeSlider) {
          volumeSlider.addEventListener('input', this._handleVolumeChangeBound);
          volumeSlider.addEventListener('input', this._updateVolumeSliderBound);
          volumeSlider.addEventListener('mouseenter', this._showVolumePercentageBound);
          volumeSlider.addEventListener('mousemove', this._showVolumePercentageBound);
          volumeSlider.addEventListener('mouseleave', this._hideVolumePercentageBound);

          // Add mouse wheel volume control
          volumeControl.addEventListener('wheel', this._handleVolumeWheelBound);

          // Initialize slider value to match video volume (convert from 0-1 to 0-100)
          volumeSlider.value = this.video.volume * 100;
          // Initialize the visual fill
          this.updateVolumeSlider();
        }
      }

      // Picture in picture
      const pipButton = this.controls.querySelector('.video-pip-button');
      if (pipButton && document.pictureInPictureEnabled) {
        pipButton.addEventListener('click', this._togglePictureInPictureBound);
      } else if (pipButton) {
        pipButton.style.display = 'none';
      }
    }

    // Mouse enter/leave for controls
    this.addEventListener('mouseenter', this._showControlsBound);
    this.addEventListener('mouseleave', this._hideControlsBound);

    document.addEventListener('changeMediaGroup', this._toogleVideo);
  }

  unbindEvents() {
    if (this.poster) {
      // Use the bound method to ensure proper removal
      this.poster.removeEventListener('click', this._loadContentBound);
    }

    if (!this.video) return;

    this.video.removeEventListener('click', this._togglePlayBound);
    this.video.removeEventListener('timeupdate', this._updateProgressBarBound);
    this.video.removeEventListener('play', this._updatePlayButtonBound);
    this.video.removeEventListener('pause', this._updatePlayButtonBound);
    this.video.removeEventListener('play', this._updateControlsVisibilityBound);
    this.video.removeEventListener('pause', this._updateControlsVisibilityBound);
    this.video.removeEventListener('volumechange', this._updateVolumeIconBound);
    this.video.removeEventListener('dblclick', this._handleDoubleClickBound);

    // Remove buffering event listeners
    this.video.removeEventListener('waiting', this._handleVideoBoundingBound);
    this.video.removeEventListener('playing', this._handleVideoBoundingBound);
    this.video.removeEventListener('canplay', this._handleVideoBoundingBound);

    if (this.centerPlayButton) {
      this.centerPlayButton.removeEventListener('click', this._togglePlayBound);
    }

    if (this.controls) {
      const playButton = this.controls.querySelector('.video-play-button');
      if (playButton) {
        playButton.removeEventListener('click', this._togglePlayBound);
      }

      const seekBackward = this.controls.querySelector('.video-seek-backward');
      if (seekBackward) {
        seekBackward.removeEventListener('click', this._seekBackwardBound);
      }

      const seekForward = this.controls.querySelector('.video-seek-forward');
      if (seekForward) {
        seekForward.removeEventListener('click', this._seekForwardBound);
      }

      const progressContainer = this.controls.querySelector('.leo-video-progress-container');
      if (progressContainer) {
        progressContainer.removeEventListener('click', this._scrubBound);
      }

      const volumeControl = this.controls.querySelector('.video-volume-control');
      if (volumeControl) {
        const volumeButton = volumeControl.querySelector('.video-volume-button');
        if (volumeButton) {
          volumeButton.removeEventListener('click', this._toggleMuteBound);
        }

        const volumeSlider = volumeControl.querySelector('.video-volume-slider');
        if (volumeSlider) {
          volumeSlider.removeEventListener('input', this._handleVolumeChangeBound);
          volumeSlider.removeEventListener('input', this._updateVolumeSliderBound);
          volumeSlider.removeEventListener('mouseenter', this._showVolumePercentageBound);
          volumeSlider.removeEventListener('mousemove', this._showVolumePercentageBound);
          volumeSlider.removeEventListener('mouseleave', this._hideVolumePercentageBound);
        }

        volumeControl.removeEventListener('wheel', this._handleVolumeWheelBound);
      }

      const pipButton = this.controls.querySelector('.video-pip-button');
      if (pipButton) {
        pipButton.removeEventListener('click', this._togglePictureInPictureBound);
      }
    }

    this.removeEventListener('mouseenter', this._showControlsBound);
    this.removeEventListener('mouseleave', this._hideControlsBound);
    document.removeEventListener('changeMediaGroup', this._toogleVideo);
  }

  loadContent(callTogglePlay) {
    try {
      window.pauseAllMedia();
      if (!this.loaded) {
        // Update both the attribute and the property
        this.loaded = true;
        this.classList.add('loaded');
        this.setAttribute('loaded', '');
        // Hide the poster immediately on first click
        if (this.poster) {
          this.poster.classList.add('leo-hidden');
        }

        if (this.video) {
          // Configure the video element
          this.video.classList.remove('leo-hidden');
        }
        if (callTogglePlay?.target) {
          this.togglePlay();
        }
      }
    } catch (error) {
      console.error('Error loading content:', error);
    }
  }

  // Handle video buffering state
  handleVideoBuffering(event) {
    if (!this.loadingSpinner) return;

    if (event.type === 'waiting') {
      // Video is buffering - show spinner
      this.loadingSpinner.classList.add('visible');
    } else {
      // Video is playing/canplay - hide spinner
      this.loadingSpinner.classList.remove('visible');
    }
  }

  // Update volume slider visual fill
  updateVolumeSlider() {
    if (!this.controls) return;

    const volumeSlider = this.controls.querySelector('.video-volume-slider');
    const volumeSliderFill = this.controls.querySelector('.video-volume-slider-fill');
    const volumePercentage = this.controls.querySelector('.video-volume-percentage');

    if (volumeSlider && volumeSliderFill) {
      const percent = volumeSlider.value;
      volumeSliderFill.style.width = `${ percent }%`;

      if (volumePercentage) {
        volumePercentage.textContent = `${ Math.round(percent) }%`;
        // Position the volume percentage tooltip at the thumb position
        volumePercentage.style.left = `${ percent }%`;
      }
    }
  }

  // Show volume percentage tooltip
  showVolumePercentage(event) {
    if (!this.controls) return;

    const volumeSlider = this.controls.querySelector('.video-volume-slider');
    const volumePercentage = this.controls.querySelector('.video-volume-percentage');

    if (volumeSlider && volumePercentage) {
      const sliderRect = volumeSlider.getBoundingClientRect();
      const offsetX = event.clientX - sliderRect.left;
      const percent = Math.round((offsetX / sliderRect.width) * 100);

      // Only update position on mousemove - value is already updated on input
      if (event.type === 'mousemove' || event.type === 'mouseenter') {
        volumePercentage.style.left = `${ offsetX }px`;
      }

      volumePercentage.style.opacity = '1';
    }
  }

  // Hide volume percentage tooltip
  hideVolumePercentage() {
    if (!this.controls) return;

    const volumePercentage = this.controls.querySelector('.video-volume-percentage');

    if (volumePercentage) {
      volumePercentage.style.opacity = '0';
    }
  }

  // Handle mouse wheel volume control
  handleVolumeWheel(event) {
    if (!this.video || !this.controls) return;

    const volumeSlider = this.controls.querySelector('.video-volume-slider');
    if (!volumeSlider) return;

    event.preventDefault();

    const delta = Math.sign(event.deltaY) * -5; // Adjust volume by 5% per wheel step
    const newVolume = Math.min(Math.max(parseInt(volumeSlider.value) + delta, 0), 100);

    volumeSlider.value = newVolume;
    this.video.volume = newVolume / 100;
    this.video.muted = newVolume === 0;

    // Update the visual slider fill
    this.updateVolumeSlider();
  }

  // Video controls functionality
  togglePlay() {
    if (!this.video) return;

    if (this.video.paused) {
      this.loadContent(false);
      this.video.play();
      this.video.focus();
      if (this.centerPlayButton) this.centerPlayButton.classList.add('leo-hidden');
    } else {
      this.video.pause();
      if (this.centerPlayButton) this.centerPlayButton.classList.remove('leo-hidden');
    }
    this.updateControlsVisibility();
  }

  updatePlayButton() {
    if (!this.controls) return;

    const playIcon = this.controls.querySelector('.video-play-icon');
    const pauseIcon = this.controls.querySelector('.video-pause-icon');

    if (!playIcon || !pauseIcon) return;

    if (this.video.paused) {
      playIcon.classList.remove('leo-hidden');
      pauseIcon.classList.add('leo-hidden');
    } else {
      playIcon.classList.add('leo-hidden');
      pauseIcon.classList.remove('leo-hidden');
    }
  }

  updateControlsVisibility() {
    if (!this.controls || !this.video) return;

    if (this.video.paused) {
      // Show controls when video is paused
      this.controls.classList.remove('leo-hidden');
      this.controls.classList.add('leo-block');
    } else {
      // Hide controls when video is playing but keep progress bar and seek buttons clickable
      this.controls.classList.add('leo-hidden');
    }
  }

  showControls() {
    if (!this.controls || !this.video) return;

    if (!this.video.paused) {
      this.controls.classList.remove('leo-hidden');
      this.controls.classList.add('leo-block');
    }
  }

  hideControls() {
    if (!this.controls || !this.video) return;

    if (!this.video.paused) {
      this.controls.classList.add('leo-hidden');
      this.controls.classList.remove('leo-block');
    }
  }

  updateProgressBar() {
    if (!this.controls || !this.video) return;

    const progressBar = this.controls.querySelector('.leo-video-progress');
    const elapsed = this.controls.querySelector('.leo-video-time-elapsed');
    const remaining = this.controls.querySelector('.leo-video-time-remaining');

    if (!progressBar || !elapsed || !remaining) return;

    const percent = (this.video.currentTime / this.video.duration) * 100;
    progressBar.style.width = `${ percent }%`;

    elapsed.textContent = this.formatTime(this.video.currentTime);
    remaining.textContent = `-${ this.formatTime(this.video.duration - this.video.currentTime) }`;
  }

  formatTime(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return `${ hours.toString().padStart(1, '0') }:${ minutes.toString().padStart(2, '0') }:${ secs.toString().padStart(2, '0') }`;
  }

  scrub(event) {
    if (!this.controls || !this.video) return;

    const progressContainer = this.controls.querySelector('.leo-video-progress-container');
    if (!progressContainer) return;

    // Prevent event from propagating to video element and triggering play/pause
    event.stopPropagation();

    const percent = event.offsetX / progressContainer.offsetWidth;
    this.video.currentTime = percent * this.video.duration;
  }

  seek(seconds, event) {
    if (!this.video) return;

    // If event is provided, stop propagation
    if (event) {
      event.stopPropagation();
    }

    this.video.currentTime = Math.min(Math.max(this.video.currentTime + seconds, 0), this.video.duration);
  }

  handleDoubleClick(event) {
    if (!this.video) return;

    const {clientX} = event;
    const {left, width} = this.video.getBoundingClientRect();
    const seekAmount = 15; // seconds to seek
    const isLeftSide = clientX < left + width / 2;

    // If click is on left side of video, seek backward
    if (isLeftSide) {
      this.seek(-seekAmount);
      this.createSeekRipple(true, -seekAmount);
    } else { // If click is on right side, seek forward
      this.seek(seekAmount);
      this.createSeekRipple(false, seekAmount);
    }
  }

  // Create a seek ripple animation that covers the entire left or right side
  createSeekRipple(isLeft, seconds) {
    const rippleContainer = this.querySelector('.leo-seek-ripple-container');
    if (!rippleContainer) return;

    // Create ripple element
    const ripple = document.createElement('div');
    ripple.className = 'leo-seek-ripple';

    // Position at 25% for left side or 75% for right side (center of each half)
    ripple.style.left = isLeft ? '25%' : '75%';

    // Create ripple circle
    const circle = document.createElement('div');
    circle.className = 'leo-seek-ripple-circle';

    // Create content container
    const content = document.createElement('div');
    content.className = 'leo-seek-ripple-content';

    // Create icon and text elements
    const isForward = seconds > 0;
    const icon = document.createElement('div');
    icon.className = 'leo-seek-ripple-icon';

    // Set the icon SVG based on direction
    icon.innerHTML = isForward
      ? '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 17V15L15 17.5L12.5 20V18C8.95 18 6 15.05 6 11.5C6 7.95 8.95 5 12.5 5C16.05 5 19 7.95 19 11.5H17C17 9.15 14.85 7 12.5 7C10.15 7 8 9.15 8 11.5C8 13.85 10.15 16 12.5 16V17Z" fill="white"/></svg>'
      : '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 7V9L10 6.5L12.5 4V6C16.05 6 19 8.95 19 12.5C19 16.05 16.05 19 12.5 19C8.95 19 6 16.05 6 12.5H8C8 14.85 10.15 17 12.5 17C14.85 17 17 14.85 17 12.5C17 10.15 14.85 8 12.5 8V7Z" fill="white"/></svg>';

    // Create text showing seconds
    const text = document.createElement('div');
    text.className = 'leo-seek-ripple-text';
    text.textContent = `${ isForward ? '+' : '' }${ seconds }s`;

    // Assemble the ripple
    content.appendChild(icon);
    content.appendChild(text);
    circle.appendChild(content);
    ripple.appendChild(circle);
    rippleContainer.appendChild(ripple);

    // Make the ripple visible
    requestAnimationFrame(() => {
      ripple.style.opacity = '1';
    });

    // Remove after animation completes
    setTimeout(() => {
      if (ripple && ripple.parentNode) {
        ripple.parentNode.removeChild(ripple);
      }
    }, 750); // Match the CSS animation duration
  }

  toggleMute() {
    if (!this.video || !this.controls) return;

    this.video.muted = !this.video.muted;
    this.video.volume = this.video.muted ? 0 : parseFloat(this.controls.querySelector('.video-volume-slider').value) / 100;

    // Update the slider value
    const volumeSlider = this.controls.querySelector('.video-volume-slider');
    if (volumeSlider) {
      if (this.video.muted) {
        volumeSlider.setAttribute('data-previous-volume', volumeSlider.value);
        volumeSlider.value = 0;
      } else {
        const previousVolume = volumeSlider.getAttribute('data-previous-volume') || 100;
        volumeSlider.value = previousVolume;
        this.video.volume = parseFloat(previousVolume) / 100;
      }
    }

    // Update the visual slider fill
    this.updateVolumeSlider();
  }

  handleVolumeChange(event) {
    if (!this.video) return;

    const value = parseFloat(event.target.value) / 100;
    this.video.volume = value;
    this.video.muted = value === 0;

    if (value > 0) {
      event.target.setAttribute('data-previous-volume', event.target.value);
    }

    // Update the visual slider fill
    this.updateVolumeSlider();
  }

  updateVolumeIcon() {
    if (!this.controls) return;

    const volumeHigh = this.controls.querySelector('.video-volume-high');
    const volumeLow = this.controls.querySelector('.video-volume-low');
    const volumeMuted = this.controls.querySelector('.video-volume-muted');
    const volumeSlider = this.controls.querySelector('.video-volume-slider');

    if (!volumeHigh || !volumeLow || !volumeMuted) return;

    // Ensure slider value matches video volume
    if (volumeSlider && this.video && !isNaN(this.video.volume)) {
      volumeSlider.value = this.video.muted ? 0 : this.video.volume * 100;
      // Update the visual fill when volume icon changes
      this.updateVolumeSlider();
    }

    // Hide all icons first
    volumeHigh.classList.add('leo-hidden');
    volumeLow.classList.add('leo-hidden');
    volumeMuted.classList.add('leo-hidden');

    // Show appropriate icon based on volume
    if (!this.video || this.video.muted || this.video.volume === 0) {
      volumeMuted.classList.remove('leo-hidden');
    } else if (this.video.volume < 0.5) {
      volumeLow.classList.remove('leo-hidden');
    } else {
      volumeHigh.classList.remove('leo-hidden');
    }
  }

  togglePictureInPicture() {
    if (!this.video) return;

    if (document.pictureInPictureElement) {
      document.exitPictureInPicture();
    } else if (document.pictureInPictureEnabled) {
      this.video.requestPictureInPicture();
    }
  }
  toogleVideo(event){
    const selectedColor = event.detail.data;
    const media = document.querySelectorAll(`.gallery-preview-wrapper [data-media-type="video"][data-media-group="${selectedColor}"]`)[0];
    if(!media) return;
    
    const mediaIdAttribute = media.dataset.mediaId;
    const mediaId = mediaIdAttribute.replace(/^.*main-/, '');
    const currentMediaId = this.dataset.mediaId;
    if(currentMediaId === mediaId){
      this.classList.remove('leo-hidden');
      this.classList.add('leo-block');
    }else{
      this.classList.add('leo-hidden');
      this.classList.remove('leo-block');
    }
    
  }
}

customElements.define('video-player', VideoPlayer);
